import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import websocketService from '../services/websocket';
import { withTransientRetry } from '../utils/transientRetry';
// DEC-1450: every path that proves a session existed on this browser marks it
// as returning, so the beta gate can offer sign-in instead of the waitlist on
// a cold return. See utils/returningBrowser.js for why it is a bare flag.
import { markReturningBrowser } from '../utils/returningBrowser';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  // DEC-1243: the profile fetch failed for a reason that is NOT "your token
  // is bad" (network drop, aborted request, 5xx). The token is still held;
  // the app shows a retry instead of pretending the user is signed out.
  const [authLoadFailed, setAuthLoadFailed] = useState(false);

  useEffect(() => {
    if (token) {
      loadUser();
      websocketService.connect(token);
    } else {
      websocketService.disconnect();
      setLoading(false);
    }
  }, [token]);

  /**
   * DEC-1243 (PROD incident): this function used to call logout() on ANY
   * error — which made it a session self-destruct button.
   *
   * The failure it caused: signing in from the beta gate stores the token,
   * flips isAuthenticated, and the gate then hard-navigates to '/' ~50ms
   * later. That navigation ABORTS this very request while it is still in
   * flight (on mobile it rarely completes inside 50ms). The abort rejected,
   * the catch ran logout(), and the freshly-minted token was erased before
   * the new page loaded — so the user arrived signed OUT and was bounced to
   * the marketing page. Verified in prod logs: successful POST /auth/login
   * with no GET /auth/profile ever reaching the server, and the token
   * present on one request and gone from the next.
   *
   * Rule now: only a definitive auth rejection (401/403) may end a session.
   * Everything else is transient — retry, and keep the token either way.
   */
  const loadUser = async () => {
    try {
      const response = await withTransientRetry(() => authAPI.getProfile());
      setUser(response.data);
      setAuthLoadFailed(false);
      // A hydrated session is proof this browser has been signed in — covers
      // every mint path (magic link, embed verify, login) at one seam.
      markReturningBrowser();
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        // The token is genuinely no longer valid — this is the only case
        // where dropping the session is correct.
        logout();
      } else {
        // Network error, aborted request (including our own navigation),
        // timeout, or 5xx. The token is NOT known-bad, so it survives; a
        // reload or the retry affordance recovers the session.
        console.error('Failed to load user (session preserved):', error?.message);
        setAuthLoadFailed(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // Lets the shell offer "Try again" without a full page reload.
  const retryLoadUser = useCallback(() => {
    if (!localStorage.getItem('token')) return;
    setAuthLoadFailed(false);
    setLoading(true);
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { token, user } = response.data;
    localStorage.setItem('token', token);
    markReturningBrowser();
    setToken(token);
    setUser(user);
    return user;
  };

  const register = async (userData) => {
    const response = await authAPI.register(userData);
    // Registration no longer returns a token - user must verify first
    return response.data;
  };

  const loginWithToken = (newToken, newUser) => {
    localStorage.setItem('token', newToken);
    // Immediate, not only via loadUser: the gate hard-reloads ~50ms after
    // sign-in and can abort the profile fetch (DEC-1243) — the marker must
    // already be down by then.
    markReturningBrowser();
    setToken(newToken);
    setUser(newUser);
  };

  const logout = () => {
    websocketService.disconnect();
    // A browser that held a session is a returning browser whether the
    // session ended by choice or by a 401 — mark before the evidence goes.
    if (localStorage.getItem('token')) markReturningBrowser();
    localStorage.removeItem('token');
    localStorage.removeItem('deco_active_org');
    setToken(null);
    setUser(null);
    setAuthLoadFailed(false);
  };

  // DEC-714.7: lets callers (e.g. Profile page after an account-type change)
  // patch the user object without re-fetching. Used for in-place updates that
  // already returned the fresh user payload from the server.
  const updateUser = (patchOrUser) => {
    setUser((prev) => ({ ...(prev || {}), ...(patchOrUser || {}) }));
  };

  const value = {
    user,
    loading,
    // DEC-1243: distinguishes "we couldn't reach DECO" from "you're signed
    // out" — the app must never render the signed-out marketing page for a
    // user who is holding a valid token.
    authLoadFailed,
    hasStoredToken: !!token,
    retryLoadUser,
    login,
    register,
    loginWithToken,
    logout,
    updateUser,
    isAuthenticated: !!user,
    isConsumer: user?.userType === 'consumer',
    isOperator: user?.userType === 'operator',
    isServiceProvider: user?.userType === 'service_provider',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
